import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Send, MessageSquare, Phone, Clock, CheckCircle, AlertCircle, Users, Bell } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../contexts";
import { apiCall } from "../lib/api";
import { toast } from "sonner";

const btn3D = "shadow-[0_6px_0_rgba(0,0,0,0.12),0_8px_16px_rgba(0,0,0,0.08)] hover:shadow-[0_4px_0_rgba(0,0,0,0.12),0_6px_12px_rgba(0,0,0,0.06)] hover:translate-y-[2px] active:shadow-[0_1px_0_rgba(0,0,0,0.12),0_2px_4px_rgba(0,0,0,0.04)] active:translate-y-[5px] transition-all duration-150 ease-out";

const TEMPLATES = [
  { id: "absence", label: "Absence Notice", icon: AlertCircle, message: "Dear Parent, your child was absent from school today. Please contact the school for more details. - YCPS Admin" },
  { id: "event", label: "Event Reminder", icon: Bell, message: "Reminder: [Event Name] is scheduled for [Date]. Please ensure your child is prepared. - YCPS" },
  { id: "fees", label: "Fee Reminder", icon: Clock, message: "Dear Parent, this is a reminder that school fees for the current term are due. Please make payment at your earliest convenience. - YCPS" },
  { id: "general", label: "General Notice", icon: MessageSquare, message: "" },
];

const SMSNotificationsPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState("general");
  const [logs, setLogs] = useState([]);
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState("compose");

  useEffect(() => {
    if (user && token && (user.user_type === "admin" || user.user_type === "teacher")) {
      fetchLogs();
    }
  }, [user, token]);

  const fetchLogs = async () => {
    try {
      const data = await apiCall("GET", "/sms/logs", null, token);
      setLogs(data);
    } catch (err) { console.error(err); }
  };

  const handleSend = async () => {
    if (!phone.trim()) { toast.error("Enter phone number"); return; }
    if (!message.trim()) { toast.error("Enter message"); return; }
    setSending(true);
    try {
      await apiCall("POST", "/sms/send", { phone, message, category }, token);
      toast.success("SMS queued! (Service not yet configured — will send when Twilio is connected)");
      setPhone(""); setMessage("");
      fetchLogs();
      setTab("logs");
    } catch (err) {
      toast.error("Failed to send");
    }
    setSending(false);
  };

  const selectTemplate = (tpl) => {
    setCategory(tpl.id);
    if (tpl.message) setMessage(tpl.message);
  };

  if (!user || (user.user_type !== "admin" && user.user_type !== "teacher")) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400 flex items-center justify-center">
        <Card className="max-w-md"><CardContent className="p-8 text-center">
          <AlertCircle size={48} className="mx-auto text-red-400 mb-3" />
          <p className="font-bold text-[#002147]">Access Denied</p>
          <p className="text-sm text-gray-500 mt-1">Only admins and teachers can access SMS notifications.</p>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400" data-testid="sms-page">
      <div className="bg-gradient-to-r from-[#002147] to-[#0a3d7a] text-white py-8">
        <div className="max-w-4xl mx-auto px-4">
          <button onClick={() => navigate(-1)} className={`inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white font-medium rounded-xl mb-4 border border-white/20 ${btn3D} cursor-pointer select-none`} data-testid="back-btn">
            <ArrowLeft size={16} /> Back
          </button>
          <div className="flex items-center gap-3">
            <Send size={28} />
            <div>
              <h1 className="text-2xl font-bold">SMS Notifications</h1>
              <p className="text-white/70 text-sm">Send messages to parents & guardians</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button onClick={() => setTab("compose")} className={`px-5 py-2.5 rounded-xl font-semibold text-sm ${tab === "compose" ? "bg-[#002147] text-white" : "bg-white/50 text-[#002147]"} ${btn3D}`} data-testid="tab-compose">
            <Send size={14} className="inline mr-1" /> Compose
          </button>
          <button onClick={() => setTab("logs")} className={`px-5 py-2.5 rounded-xl font-semibold text-sm ${tab === "logs" ? "bg-[#002147] text-white" : "bg-white/50 text-[#002147]"} ${btn3D}`} data-testid="tab-logs">
            <Clock size={14} className="inline mr-1" /> Sent Log ({logs.length})
          </button>
        </div>

        {tab === "compose" && (
          <div className="grid md:grid-cols-3 gap-5">
            {/* Templates */}
            <div className="space-y-2">
              <p className="text-xs font-bold text-[#002147]/70 uppercase tracking-wide mb-2">Quick Templates</p>
              {TEMPLATES.map((tpl) => (
                <button key={tpl.id} onClick={() => selectTemplate(tpl)}
                  className={`w-full text-left p-3 rounded-xl text-sm flex items-center gap-2 transition-all ${
                    category === tpl.id ? "bg-[#002147] text-white" : "bg-white/60 text-[#002147] hover:bg-white/80"
                  } ${btn3D}`}
                  data-testid={`template-${tpl.id}`}>
                  <tpl.icon size={16} /> {tpl.label}
                </button>
              ))}
            </div>

            {/* Compose form */}
            <Card className="md:col-span-2 shadow-lg">
              <CardContent className="p-5 space-y-4">
                <div>
                  <label className="text-xs font-bold text-[#002147]/60 uppercase tracking-wide">Phone Number</label>
                  <div className="flex items-center gap-2 mt-1">
                    <Phone size={16} className="text-gray-400" />
                    <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                      placeholder="+233 XX XXX XXXX" className="flex-1 px-4 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-blue-400"
                      data-testid="sms-phone-input" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold text-[#002147]/60 uppercase tracking-wide">Message</label>
                  <textarea value={message} onChange={(e) => setMessage(e.target.value)}
                    rows={4} placeholder="Type your message..."
                    className="w-full mt-1 px-4 py-3 border rounded-xl text-sm resize-none focus:ring-2 focus:ring-blue-400"
                    data-testid="sms-message-input" />
                  <p className="text-right text-[10px] text-gray-400">{message.length}/160 characters</p>
                </div>
                <button onClick={handleSend} disabled={sending}
                  className={`w-full py-3 bg-green-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 disabled:opacity-50 ${btn3D}`}
                  data-testid="send-sms-btn">
                  <Send size={16} /> {sending ? "Sending..." : "Send SMS"}
                </button>
                <p className="text-[10px] text-gray-400 text-center">SMS service is MOCKED. Connect Twilio to enable real messages.</p>
              </CardContent>
            </Card>
          </div>
        )}

        {tab === "logs" && (
          <div className="space-y-3">
            {logs.length === 0 && (
              <div className="text-center py-12">
                <MessageSquare size={48} className="mx-auto text-[#002147]/20 mb-3" />
                <p className="text-[#002147]/50 font-medium">No messages sent yet</p>
              </div>
            )}
            {logs.map((log) => (
              <Card key={log.id} className="hover:shadow-md transition-shadow" data-testid={`sms-log-${log.id}`}>
                <CardContent className="p-4 flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <MessageSquare size={16} className="text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm text-[#002147]">{log.phone}</span>
                      <Badge className="bg-yellow-100 text-yellow-700 text-[10px]">{log.status}</Badge>
                    </div>
                    <p className="text-sm text-gray-600 truncate">{log.message}</p>
                    <p className="text-[10px] text-gray-400 mt-1">{new Date(log.created_at).toLocaleString()}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SMSNotificationsPage;
